import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { 
  FiPlusCircle, FiMinusCircle, FiBarChart2, FiCheck, FiX,
  FiUpload, FiDownload, FiEdit, FiTrash2, FiPlus, FiInfo,
  FiAlertCircle, FiAlertTriangle
} = FiIcons;

const DistractorAnalysis = ({ questions }) => {
  const [responseData, setResponseData] = useState({});
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [fileData, setFileData] = useState(null);
  const [uploadStatus, setUploadStatus] = useState({ loading: false, error: null });
  const [analysisResults, setAnalysisResults] = useState(null);
  const [manualEntryMode, setManualEntryMode] = useState(false);
  
  // Filter only multiple choice questions
  const mcqQuestions = questions.filter(q => q.type === 'Multiple Choice' || (q.options && q.options.length > 0));
  
  // Generate placeholder response data structure for manual entry
  const generateEmptyResponseData = () => {
    const emptyData = {};
    mcqQuestions.forEach(question => {
      emptyData[question.id] = {
        questionId: question.id,
        responses: Array(5).fill().map(() => ({
          studentId: '',
          selectedOption: '',
          isCorrect: false
        }))
      };
    });
    return emptyData;
  };
  
  // Initialize manual entry data
  const initializeManualEntry = () => {
    setManualEntryMode(true);
    setResponseData(generateEmptyResponseData());
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadStatus({ loading: true, error: null });
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        setFileData(data);
        processResponseData(data);
      } catch (error) {
        setUploadStatus({ loading: false, error: 'Invalid file format. Please upload a valid JSON file.' });
      }
    };
    
    reader.onerror = () => {
      setUploadStatus({ loading: false, error: 'Error reading file.' });
    };
    
    reader.readAsText(file);
  };

  // Process the response data and run distractor analysis
  const processResponseData = (data) => {
    try {
      // Validate data structure
      if (!data || !data.responses || !Array.isArray(data.responses)) {
        throw new Error('Invalid data structure');
      }
      
      // Map responses to questions
      const mappedResponses = {};
      data.responses.forEach(response => {
        if (!mappedResponses[response.questionId]) {
          mappedResponses[response.questionId] = {
            questionId: response.questionId,
            responses: []
          };
        }
        mappedResponses[response.questionId].responses.push(response);
      });
      
      setResponseData(mappedResponses);
      runDistractorAnalysis(mappedResponses);
      
      setUploadStatus({ loading: false, error: null });
      setShowUploadModal(false);
    } catch (error) {
      setUploadStatus({ 
        loading: false, 
        error: `Error processing data: ${error.message}`
      });
    }
  };

  // Run distractor analysis on the response data
  const runDistractorAnalysis = (data) => {
    const results = mcqQuestions.map(question => {
      const questionResponses = data[question.id]?.responses || [];
      const totalResponses = questionResponses.length;
      
      if (totalResponses === 0 || !question.options) {
        return {
          ...question,
          totalResponses: 0,
          optionAnalysis: [],
          effectiveness: 'unknown',
          recommendation: 'No response data available'
        };
      }
      
      // Get all options for the question
      const options = Array.isArray(question.options) 
        ? question.options 
        : question.options.split(',').map(o => o.trim());
      
      // Calculate selection frequency for each option
      const optionCounts = {};
      options.forEach(option => {
        optionCounts[option] = 0;
      });
      
      // Count responses for each option
      questionResponses.forEach(response => {
        if (response.selectedOption && optionCounts[response.selectedOption] !== undefined) {
          optionCounts[response.selectedOption]++;
        }
      });
      
      // Calculate distractor statistics
      const optionAnalysis = options.map(option => {
        const count = optionCounts[option] || 0;
        const percentage = totalResponses > 0 ? Math.round((count / totalResponses) * 100) : 0;
        const isCorrect = option === question.correctAnswer;
        
        // Calculate effectiveness for this option
        let effectiveness = 'poor';
        if (isCorrect) {
          effectiveness = percentage > 70 ? 'excellent' : percentage > 50 ? 'good' : 'poor';
        } else {
          // For distractors, some selection is good but not too much
          effectiveness = percentage > 0 && percentage < 30 ? 'good' : percentage === 0 ? 'poor' : 'acceptable';
        }
        
        return {
          option,
          count,
          percentage,
          isCorrect,
          effectiveness
        };
      });
      
      // Overall distractor effectiveness
      const correctOption = optionAnalysis.find(o => o.isCorrect);
      const distractors = optionAnalysis.filter(o => !o.isCorrect);
      
      // Check if any distractor is working effectively (has some responses)
      const workingDistractors = distractors.filter(d => d.count > 0);
      const correctPercentage = correctOption ? correctOption.percentage : 0;
      
      let effectiveness = 'poor';
      let recommendation = '';
      
      if (correctPercentage > 70 && workingDistractors.length > 0) {
        effectiveness = 'excellent';
        recommendation = 'Well-functioning question with effective distractors';
      } else if (correctPercentage > 50 && workingDistractors.length > 0) {
        effectiveness = 'good';
        recommendation = 'Good question, but could improve some distractors';
      } else if (correctPercentage < 30) {
        effectiveness = 'poor';
        recommendation = 'Question may be too difficult or confusing';
      } else if (workingDistractors.length === 0) {
        effectiveness = 'poor';
        recommendation = 'Distractors are not functioning, revise options';
      } else {
        effectiveness = 'acceptable';
        recommendation = 'Question functions but needs improvement';
      }
      
      return {
        ...question,
        totalResponses,
        optionAnalysis,
        effectiveness,
        recommendation
      };
    });
    
    // Calculate summary statistics
    const validResults = results.filter(r => r.totalResponses > 0);
    
    const summaryStats = {
      totalQuestions: validResults.length,
      excellentItems: validResults.filter(q => q.effectiveness === 'excellent').length,
      goodItems: validResults.filter(q => q.effectiveness === 'good').length,
      acceptableItems: validResults.filter(q => q.effectiveness === 'acceptable').length,
      poorItems: validResults.filter(q => q.effectiveness === 'poor').length,
      totalResponses: Object.values(data).reduce((sum, q) => sum + q.responses.length, 0)
    };
    
    setAnalysisResults({
      items: results,
      summary: summaryStats
    });
  };

  // Handle manual entry form changes
  const handleResponseChange = (questionId, responseIndex, field, value) => {
    setResponseData(prev => {
      const updatedData = { ...prev };
      const question = { ...updatedData[questionId] };
      const responses = [...question.responses];
      
      // For isCorrect field, convert string to boolean
      const processedValue = field === 'isCorrect' ? value === 'true' : value;
      
      // If changing selectedOption, update isCorrect based on correctAnswer
      let updatedResponse = { ...responses[responseIndex], [field]: processedValue };
      
      if (field === 'selectedOption') {
        const questionData = mcqQuestions.find(q => q.id === questionId);
        updatedResponse.isCorrect = value === questionData?.correctAnswer;
      }
      
      responses[responseIndex] = updatedResponse;
      question.responses = responses;
      updatedData[questionId] = question;
      return updatedData;
    });
  };

  // Add a new response row to a question
  const addResponseRow = (questionId) => {
    setResponseData(prev => {
      const updatedData = { ...prev };
      const question = { ...updatedData[questionId] };
      const responses = [...question.responses, {
        studentId: `Student${question.responses.length + 1}`,
        selectedOption: '',
        isCorrect: false
      }];
      question.responses = responses;
      updatedData[questionId] = question;
      return updatedData;
    });
  };

  // Remove a response row from a question
  const removeResponseRow = (questionId, responseIndex) => {
    setResponseData(prev => {
      const updatedData = { ...prev };
      const question = { ...updatedData[questionId] };
      const responses = question.responses.filter((_, idx) => idx !== responseIndex);
      question.responses = responses;
      updatedData[questionId] = question;
      return updatedData;
    });
  };

  // Run analysis on manual entry data
  const runManualAnalysis = () => {
    runDistractorAnalysis(responseData);
  };

  // Export response data as JSON
  const exportResponseData = () => {
    const dataStr = JSON.stringify({
      responses: Object.values(responseData).flatMap(q => q.responses.map(r => ({
        questionId: q.questionId,
        ...r
      })))
    }, null, 2);
    
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mcq_response_data.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  // Get effectiveness color
  const getEffectivenessColor = (effectiveness) => {
    switch (effectiveness) {
      case 'excellent': return 'text-green-600';
      case 'good': return 'text-blue-600';
      case 'acceptable': return 'text-yellow-600';
      case 'poor': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };
  
  // Get effectiveness icon
  const getEffectivenessIcon = (effectiveness) => {
    switch (effectiveness) {
      case 'excellent': return FiCheck;
      case 'good': return FiCheck;
      case 'acceptable': return FiAlertCircle;
      case 'poor': return FiX;
      default: return FiInfo;
    }
  };
  
  // Sample response data structure
  const sampleData = {
    "responses": [
      {
        "questionId": "q1",
        "studentId": "s1",
        "selectedOption": "Paris",
        "isCorrect": true
      },
      {
        "questionId": "q1",
        "studentId": "s2",
        "selectedOption": "London",
        "isCorrect": false
      }
    ]
  };
  
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 flex items-center">
            <div className="p-2 bg-gradient-to-br from-red-400 to-red-600 rounded-lg text-white mr-3">
              <SafeIcon icon={FiPlusCircle} />
            </div>
            Distractor Analysis (MCQs)
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Analyzes how effective each option is in multiple-choice questions
          </p>
        </div>
        <div className="bg-red-50 text-red-600 px-3 py-1 rounded-full text-xs font-medium">
          MCQs Only
        </div>
      </div>

      {mcqQuestions.length === 0 ? (
        <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200 text-center">
          <SafeIcon icon={FiAlertTriangle} className="mx-auto text-4xl text-yellow-500 mb-4" />
          <h3 className="text-xl font-medium text-yellow-800 mb-2">No Multiple Choice Questions</h3>
          <p className="text-yellow-700">
            Distractor analysis requires multiple choice questions. Please add or select MCQs to analyze.
          </p>
        </div>
      ) : !analysisResults && (
        <div className="mb-6 p-5 bg-red-50 rounded-lg border border-red-100">
          <h4 className="text-md font-semibold text-red-800 mb-3">
            Response Data Required
          </h4>
          <p className="text-sm text-red-700 mb-4">
            Distractor analysis requires student response data to evaluate option effectiveness. Please upload response data or enter it manually.
          </p>
          
          <div className="flex flex-wrap gap-4 mt-4">
            <motion.button
              onClick={() => setShowUploadModal(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg flex items-center space-x-2 hover:bg-red-700"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <SafeIcon icon={FiUpload} />
              <span>Upload Response Data</span>
            </motion.button>
            
            <motion.button
              onClick={initializeManualEntry}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg flex items-center space-x-2 hover:bg-purple-700"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <SafeIcon icon={FiEdit} />
              <span>Enter Response Data Manually</span>
            </motion.button>
            
            <motion.button
              onClick={() => window.open('data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(sampleData, null, 2)))}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg flex items-center space-x-2 hover:bg-gray-700"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <SafeIcon icon={FiDownload} />
              <span>Download Sample Format</span>
            </motion.button>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }} 
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg p-6 max-w-lg w-full"
          >
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Upload MCQ Response Data</h3>
            
            <p className="text-sm text-gray-600 mb-4">
              Upload a JSON file containing student responses to multiple-choice questions. The file should follow the structure shown in the example below.
            </p>
            
            <div className="bg-gray-50 p-3 rounded-lg mb-4 overflow-x-auto">
              <pre className="text-xs text-gray-700">
                {JSON.stringify(sampleData, null, 2)}
              </pre>
            </div>
            
            <input 
              type="file" 
              accept=".json" 
              onChange={handleFileUpload}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-lg file:border-0
                file:text-sm file:font-semibold
                file:bg-red-50 file:text-red-700
                hover:file:bg-red-100
                mb-4"
            />
            
            {uploadStatus.error && (
              <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4">
                {uploadStatus.error}
              </div>
            )}
            
            <div className="flex justify-end space-x-3">
              <button 
                onClick={() => setShowUploadModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                disabled={uploadStatus.loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg flex items-center space-x-2 hover:bg-red-700 disabled:bg-gray-400"
              >
                {uploadStatus.loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <span>Upload</span>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Manual Entry Form */}
      {manualEntryMode && !analysisResults && (
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-md font-semibold text-gray-800">
              Manual MCQ Response Data Entry
            </h4>
            <div className="flex space-x-3">
              <motion.button
                onClick={runManualAnalysis}
                className="px-4 py-2 bg-green-600 text-white rounded-lg flex items-center space-x-2 hover:bg-green-700"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <SafeIcon icon={FiBarChart2} />
                <span>Run Analysis</span>
              </motion.button>
              
              <motion.button
                onClick={exportResponseData}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center space-x-2 hover:bg-blue-700"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <SafeIcon icon={FiDownload} />
                <span>Export Data</span>
              </motion.button>
            </div>
          </div>
          
          <div className="space-y-6 max-h-96 overflow-y-auto p-2">
            {mcqQuestions.map((question, qIndex) => {
              // Get all options for this question
              const options = Array.isArray(question.options) 
                ? question.options 
                : question.options?.split(',').map(o => o.trim()) || [];
              
              return (
                <div key={question.id} className="border border-gray-200 rounded-lg p-4">
                  <h5 className="font-medium text-gray-800 mb-2">
                    Question {qIndex + 1}: {question.text?.substring(0, 100)}...
                  </h5>
                  
                  <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-700 mb-2">
                      <span className="font-medium">Options:</span> {options.join(', ')}
                    </p>
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Correct Answer:</span> {question.correctAnswer}
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 p-3 rounded-lg mb-3">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2">Student ID</th>
                          <th className="text-left py-2">Selected Option</th>
                          <th className="text-left py-2">Is Correct?</th>
                          <th className="text-left py-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {responseData[question.id]?.responses.map((response, rIndex) => (
                          <tr key={rIndex} className="border-b border-gray-100">
                            <td className="py-2">
                              <input
                                type="text"
                                value={response.studentId}
                                onChange={(e) => handleResponseChange(question.id, rIndex, 'studentId', e.target.value)}
                                className="w-full p-1 border border-gray-300 rounded"
                                placeholder="Student ID"
                              />
                            </td>
                            <td className="py-2">
                              <select
                                value={response.selectedOption}
                                onChange={(e) => handleResponseChange(question.id, rIndex, 'selectedOption', e.target.value)}
                                className="w-full p-1 border border-gray-300 rounded"
                              >
                                <option value="">Select an option</option>
                                {options.map((option, idx) => (
                                  <option key={idx} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="py-2">
                              <div className={`px-2 py-1 rounded text-xs inline-block ${
                                response.isCorrect ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {response.isCorrect ? 'Correct' : 'Incorrect'}
                              </div>
                            </td>
                            <td className="py-2">
                              <button
                                onClick={() => removeResponseRow(question.id, rIndex)}
                                className="p-1 text-red-600 hover:text-red-800"
                                title="Remove response"
                              >
                                <SafeIcon icon={FiTrash2} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    
                    <button
                      onClick={() => addResponseRow(question.id)}
                      className="mt-2 px-3 py-1 bg-gray-200 text-gray-700 rounded flex items-center space-x-1 text-sm hover:bg-gray-300"
                    >
                      <SafeIcon icon={FiPlus} className="text-xs" />
                      <span>Add Response</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Analysis Results */}
      {analysisResults && (
        <>
          {/* Reset Button */}
          <div className="mb-4 flex justify-end">
            <motion.button
              onClick={() => {
                setAnalysisResults(null);
                setManualEntryMode(false);
                setResponseData({});
              }}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg flex items-center space-x-2 hover:bg-gray-700"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <SafeIcon icon={FiX} />
              <span>Reset Analysis</span>
            </motion.button>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gradient-to-br from-red-50 to-orange-50 p-4 rounded-lg border border-red-100">
              <div className="text-sm text-red-800 font-medium mb-1">Total MCQs Analyzed</div>
              <div className="text-2xl font-bold text-red-900">{analysisResults.summary.totalQuestions}</div>
              <div className="text-xs text-red-700 mt-1">
                From {analysisResults.summary.totalResponses} total student responses
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-lg border border-green-100">
              <div className="text-sm text-green-800 font-medium mb-1">Excellent/Good Items</div>
              <div className="text-2xl font-bold text-green-900">
                {analysisResults.summary.excellentItems + analysisResults.summary.goodItems}
              </div>
              <div className="text-xs text-green-700 mt-1">
                {Math.round(((analysisResults.summary.excellentItems + analysisResults.summary.goodItems) / analysisResults.summary.totalQuestions) * 100)}% of all MCQs
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-yellow-50 to-amber-50 p-4 rounded-lg border border-yellow-100">
              <div className="text-sm text-yellow-800 font-medium mb-1">Acceptable Items</div>
              <div className="text-2xl font-bold text-yellow-900">{analysisResults.summary.acceptableItems}</div>
              <div className="text-xs text-yellow-700 mt-1">
                {Math.round((analysisResults.summary.acceptableItems / analysisResults.summary.totalQuestions) * 100)}% of all MCQs
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-red-50 to-rose-50 p-4 rounded-lg border border-red-100">
              <div className="text-sm text-red-800 font-medium mb-1">Poor Items</div>
              <div className="text-2xl font-bold text-red-900">{analysisResults.summary.poorItems}</div>
              <div className="text-xs text-red-700 mt-1">
                {Math.round((analysisResults.summary.poorItems / analysisResults.summary.totalQuestions) * 100)}% need significant revision
              </div>
            </div>
          </div>

          {/* Detailed Results */}
          <div className="space-y-6">
            {analysisResults.items.map((item, index) => (
              <div key={item.id} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Question Header */}
                <div className={`p-4 ${
                  item.totalResponses === 0 ? 'bg-gray-50' :
                  item.effectiveness === 'excellent' ? 'bg-green-50' :
                  item.effectiveness === 'good' ? 'bg-blue-50' :
                  item.effectiveness === 'acceptable' ? 'bg-yellow-50' :
                  'bg-red-50'
                }`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-semibold text-gray-800">Question {index + 1}: {item.text?.substring(0, 100)}{item.text?.length > 100 ? '...' : ''}</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {item.totalResponses > 0 ? (
                          <span>
                            Based on {item.totalResponses} student responses
                          </span>
                        ) : (
                          <span className="text-gray-500">No response data available</span>
                        )}
                      </p>
                    </div>
                    {item.totalResponses > 0 && (
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                        item.effectiveness === 'excellent' ? 'bg-green-100 text-green-800' :
                        item.effectiveness === 'good' ? 'bg-blue-100 text-blue-800' :
                        item.effectiveness === 'acceptable' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {item.effectiveness.charAt(0).toUpperCase() + item.effectiveness.slice(1)}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Option Analysis */}
                {item.totalResponses > 0 ? (
                  <div className="p-4">
                    <div className="mb-3">
                      <p className="font-medium text-sm text-gray-700">Option Analysis</p>
                    </div>
                    
                    <div className="space-y-3">
                      {item.optionAnalysis?.map((option, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center">
                              <div className={`w-3 h-3 rounded-full mr-2 ${option.isCorrect ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                              <span className={`font-medium ${option.isCorrect ? 'text-green-700' : 'text-gray-700'}`}>
                                {option.option} {option.isCorrect && '(Correct Answer)'}
                              </span>
                            </div>
                            <div className={`text-xs px-2 py-1 rounded ${
                              option.effectiveness === 'excellent' ? 'bg-green-100 text-green-800' :
                              option.effectiveness === 'good' ? 'bg-blue-100 text-blue-800' :
                              option.effectiveness === 'acceptable' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {option.effectiveness}
                            </div>
                          </div>
                          
                          <div className="relative h-6 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className={`absolute top-0 left-0 h-full ${
                                option.isCorrect ? 'bg-green-500' : 'bg-blue-500'
                              }`}
                              style={{ width: `${option.percentage}%` }}
                            ></div>
                            <div className="absolute inset-0 flex items-center justify-end pr-2">
                              <span className="text-xs font-medium text-gray-800">
                                {option.count} responses ({option.percentage}%)
                              </span>
                            </div>
                          </div>
                          
                          {option.isCorrect ? (
                            <p className="text-xs text-gray-600 mt-2">
                              {option.percentage > 70 ? (
                                "Excellent - Most students selected the correct answer"
                              ) : option.percentage > 50 ? (
                                "Good - Majority of students selected the correct answer"
                              ) : (
                                "Poor - Too few students selected the correct answer"
                              )}
                            </p>
                          ) : (
                            <p className="text-xs text-gray-600 mt-2">
                              {option.percentage === 0 ? (
                                "Poor - This distractor is not attracting any responses"
                              ) : option.percentage > 30 ? (
                                "Caution - This distractor may be too attractive or confusing"
                              ) : (
                                "Good - This distractor is functioning as intended"
                              )}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-4 p-3 rounded-lg bg-gray-50">
                      <p className="flex items-center text-sm font-medium text-gray-700">
                        <SafeIcon icon={getEffectivenessIcon(item.effectiveness)} className={`mr-2 ${getEffectivenessColor(item.effectiveness)}`} />
                        <span>Recommendation: {item.recommendation}</span>
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 text-center text-gray-500">
                    <SafeIcon icon={FiAlertCircle} className="mx-auto text-2xl mb-2" />
                    <p>No response data available for this question</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Information Section */}
      <div className="mt-6 bg-red-50 p-4 rounded-lg border border-red-100">
        <h4 className="text-sm font-semibold text-red-800 mb-2 flex items-center">
          <SafeIcon icon={FiInfo} className="mr-2" />
          About Distractor Analysis
        </h4>
        <p className="text-xs text-red-700">
          Distractor analysis evaluates how well each option in a multiple-choice question functions. 
          Effective distractors should attract lower-performing students while being avoided by 
          higher-performing students. The correct answer should be selected by most high-performing students.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
          <div className="bg-white p-3 rounded border border-red-100">
            <h5 className="text-xs font-medium text-red-800">Correct Answer</h5>
            <p className="text-xs text-gray-600">
              Should be chosen by &gt;70% of students for excellent items, &gt;50% for good items.
            </p>
          </div>
          <div className="bg-white p-3 rounded border border-red-100">
            <h5 className="text-xs font-medium text-red-800">Effective Distractors</h5>
            <p className="text-xs text-gray-600">
              Should attract some responses (5-30%) but not be more attractive than the correct answer.
            </p>
          </div>
          <div className="bg-white p-3 rounded border border-red-100">
            <h5 className="text-xs font-medium text-red-800">Non-functioning Distractors</h5>
            <p className="text-xs text-gray-600">
              Options with 0% selection should be revised or replaced with more plausible alternatives.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DistractorAnalysis;